import { useState, useMemo, useEffect } from 'react'
import { loadStripe } from '@stripe/stripe-js'
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js'

const stripePromise = loadStripe(import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY || 'pk_test_51SZ28hE7iNjc7ECgkHxu7mepeDnGsMsyaTiAKEpNvf5QMiai1jL8PAEYCFK1OWdYJjDxdyOiJjd7gD8FAWmK0yrA00AwBZMOzD')

function BookingForm({ seats, apiUrl, onBookingComplete, selectedSeat, translations: t }) {
  const stripe = useStripe()
  const elements = useElements()
  const [formData, setFormData] = useState({
    seatId: selectedSeat?.id || '',
    bookingDate: '',
    endDate: '',
    timeSlot: '',
    startTime: '',
    endTime: '',
    paymentMethod: '',
    guestName: '',
    guestEmail: '',
    guestPhone: '',
    notes: '',
    upiReferenceNumber: ''
  })
  const [submitting, setSubmitting] = useState(false)
  const [result, setResult] = useState(null)
  const [upiReference, setUpiReference] = useState('')

  // Update seatId when selectedSeat changes
  useEffect(() => {
    if (selectedSeat) {
      setFormData(prev => ({ ...prev, seatId: selectedSeat.id }))
    }
  }, [selectedSeat])

  const availableSeats = seats.filter(seat => seat.is_available)

  const timeSlots = [
    { value: 'morning', label: 'Morning Shift (06:00 AM - 01:00 PM)', start: '06:00', end: '13:00', price: 600 },
    { value: 'evening', label: 'Evening Shift (01:00 PM - 08:00 PM)', start: '13:00', end: '20:00', price: 600 },
    { value: 'fullday', label: 'Full Day (06:00 AM - 08:00 PM)', start: '06:00', end: '20:00', price: 1000 }
  ]

  const handleChange = (e) => {
    const { name, value } = e.target
    
    // If time slot changes, update start and end times
    if (name === 'timeSlot') {
      const slot = timeSlots.find(s => s.value === value)
      if (slot) {
        setFormData(prev => ({
          ...prev,
          timeSlot: value,
          startTime: slot.start,
          endTime: slot.end
        }))
        return
      }
    }
    
    // Generate UPI reference when name is entered and UPI is selected - TEMPORARILY DISABLED
    /* if (name === 'guestName' && formData.paymentMethod === 'upi' && value.trim()) {
      const timestamp = Date.now().toString().slice(-6)
      const namePrefix = value.trim().substring(0, 3).toUpperCase().replace(/[^A-Z]/g, '')
      const reference = `${namePrefix}${timestamp}`
      setUpiReference(reference)
      setFormData(prev => ({
        ...prev,
        [name]: value,
        upiReferenceNumber: reference
      }))
      return
    }

    // Regenerate reference if payment method changes to UPI
    if (name === 'paymentMethod' && value === 'upi' && formData.guestName.trim()) {
      const timestamp = Date.now().toString().slice(-6)
      const namePrefix = formData.guestName.trim().substring(0, 3).toUpperCase().replace(/[^A-Z]/g, '')
      const reference = `${namePrefix}${timestamp}`
      setUpiReference(reference)
      setFormData(prev => ({
        ...prev,
        [name]: value,
        upiReferenceNumber: reference
      }))
      return
    } */
    
    setFormData(prev => ({
      ...prev,
      [name]: value
    }))
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setSubmitting(true)
    setResult(null)

    try {
      /* UPI Payment - Temporarily Disabled
      if (formData.paymentMethod === 'upi') {
        // UPI payment - create booking with pending status
        const response = await fetch(`${apiUrl}/bookings`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ ...formData, status: 'pending' })
        })

        const data = await response.json()

        if (response.ok) {
          setResult({
            success: true,
            message: `✅ Booking created! Please complete UPI payment with reference: ${upiReference}`,
            booking: data.data
          })
          // Reset form
          setFormData({
            seatId: '',
            bookingDate: '',
            endDate: '',
            timeSlot: '',
            startTime: '',
            endTime: '',
            paymentMethod: '',
            guestName: '',
            guestEmail: '',
            guestPhone: '',
            notes: '',
            upiReferenceNumber: ''
          })
          setUpiReference('')
          if (onBookingComplete) {
            onBookingComplete()
          }
        } else {
          setResult({
            success: false,
            message: data.error || 'Failed to create booking'
          })
        }
      } else */ {
        // Offline payment - create booking directly
        const response = await fetch(`${apiUrl}/bookings`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(formData)
        })

        const data = await response.json()

        if (response.ok) {
          setResult({
            success: true,
            message: data.message || '✅ Booking created! Please pay at the library counter within 4 hours.',
            booking: data.data
          })
          // Reset form
          setFormData({
            seatId: '',
            bookingDate: '',
            endDate: '',
            timeSlot: '',
            startTime: '',
            endTime: '',
            paymentMethod: '',
            guestName: '',
            guestEmail: '',
            guestPhone: '',
            notes: '',
            upiReferenceNumber: ''
          })
          setUpiReference('')
          if (onBookingComplete) {
            onBookingComplete()
          }
        } else {
          setResult({
            success: false,
            message: data.error || 'Failed to create booking'
          })
        }
      }
    } catch (err) {
      setResult({
        success: false,
        message: err.message || 'Error processing request'
      })
      console.error('Booking error:', err)
    } finally {
      setSubmitting(false)
    }
  }

  // Get today's date in YYYY-MM-DD format for min date (memoized)
  const today = useMemo(() => new Date().toISOString().split('T')[0], [])

  return (
    <div className="booking-widget">
      <div style={{
        backgroundColor: '#e3f2fd',
        padding: '12px',
        borderRadius: '6px',
        marginBottom: '20px',
        fontSize: '14px',
        border: '1px solid #90caf9'
      }}>
        ℹ️ <strong>Note:</strong> Seats are available for different time slots. If you see an error, the seat may already be booked for your selected time slot. Please try another time or seat.
      </div>
      <form onSubmit={handleSubmit} className="booking-form">
        {!selectedSeat && (
          <div className="form-group">
            <label htmlFor="seatId">{t.selectSeat} *</label>
            <select
              id="seatId"
              name="seatId"
              value={formData.seatId}
              onChange={handleChange}
              required
            >
              <option value="">{t.chooseSeat}</option>
              {availableSeats.map(seat => (
                <option key={seat.id} value={seat.id}>
                  {seat.seat_number} - {seat.seat_type} ({t.floor} {seat.floor})
                </option>
              ))}
            </select>
          </div>
        )}

        {selectedSeat && (
          <div className="selected-seat-info">
            <p><strong>{t.selectedSeat}:</strong> {selectedSeat.seat_number}</p>
            <p><strong>{t.type}:</strong> {selectedSeat.seat_type} | <strong>{t.floor}:</strong> {selectedSeat.floor}</p>
          </div>
        )}

        <div className="form-group">
          <label htmlFor="bookingDate">{t.startDate} *</label>
          <input
            type="date"
            id="bookingDate"
            name="bookingDate"
            value={formData.bookingDate}
            onChange={handleChange}
            min={today}
            required
          />
        </div>

        <div className="form-group">
          <label htmlFor="endDate">{t.endDate} *</label>
          <input
            type="date"
            id="endDate"
            name="endDate"
            value={formData.endDate}
            onChange={handleChange}
            min={formData.bookingDate || today}
            required
          />
        </div>

        <div className="form-group">
          <label htmlFor="timeSlot">{t.timeSlot} *</label>
          <select
            id="timeSlot"
            name="timeSlot"
            value={formData.timeSlot}
            onChange={handleChange}
            required
          >
            <option value="">{t.chooseTimeSlot}</option>
            {timeSlots.map(slot => (
              <option key={slot.value} value={slot.value}>
                {slot.label} - ₹{slot.price}
              </option>
            ))}
          </select>
        </div>

        {formData.timeSlot && (
          <div className="price-display">
            <p className="booking-price">
              💰 {t.totalAmount}: <strong>₹{timeSlots.find(s => s.value === formData.timeSlot)?.price}</strong>
            </p>
          </div>
        )}

        <div className="form-group">
          <label htmlFor="paymentMethod">{t.paymentMethod} *</label>
          <div className="payment-options">
            {/* UPI Payment - Temporarily Disabled */}
            {/* <label className="radio-option">
              <input
                type="radio"
                name="paymentMethod"
                value="upi"
                checked={formData.paymentMethod === 'upi'}
                onChange={handleChange}
                required
              />
              <span className="radio-label">
                <strong>📱 UPI Payment</strong>
                <small>Pay via Google Pay, PhonePe, Paytm</small>
              </span>
            </label> */}
            <label className="radio-option">
              <input
                type="radio"
                name="paymentMethod"
                value="offline"
                checked={formData.paymentMethod === 'offline'}
                onChange={handleChange}
                required
              />
              <span className="radio-label">
                <strong>💵 {t.offlinePayment}</strong>
                <small>{t.offlinePaymentDesc}</small>
              </span>
            </label>
          </div>
        </div>

        {formData.paymentMethod === 'offline' && (
          <div className="form-group" style={{
            backgroundColor: '#fff3e0',
            padding: '15px',
            borderRadius: '8px',
            border: '2px solid #ff9800'
          }}>
            <p style={{ margin: 0, color: '#e65100', fontWeight: 'bold' }}>
              ⏰ Important: You must complete payment at the library counter within 4 hours of booking, otherwise your booking will be automatically cancelled and the seat will be available for others.
            </p>
          </div>
        )}

        {/* UPI Payment Details - Temporarily Disabled */}
        {/* {formData.paymentMethod === 'upi' && formData.guestName && (
          <div className="form-group" style={{
            backgroundColor: '#e8f5e9',
            padding: '20px',
            borderRadius: '8px',
            border: '2px solid #4caf50'
          }}>
            <h4 style={{ margin: '0 0 15px 0', color: '#2e7d32' }}>📱 UPI Payment Details</h4>
            <div style={{ marginBottom: '12px' }}>
              <strong>Pay to UPI ID:</strong>
              <div style={{
                fontSize: '18px',
                fontWeight: 'bold',
                color: '#1976d2',
                padding: '10px',
                backgroundColor: 'white',
                borderRadius: '4px',
                marginTop: '8px',
                textAlign: 'center'
              }}>
                123456789@ybl
              </div>
            </div>
            <div style={{ marginTop: '15px' }}>
              <strong>Your Reference Number:</strong>
              <div style={{
                fontSize: '20px',
                fontWeight: 'bold',
                color: '#d32f2f',
                padding: '12px',
                backgroundColor: '#fff3e0',
                borderRadius: '4px',
                marginTop: '8px',
                textAlign: 'center',
                letterSpacing: '2px'
              }}>
                {upiReference}
              </div>
              <small style={{ display: 'block', marginTop: '8px', color: '#666' }}>
                ⚠️ Please mention this reference number in the UPI payment remarks/note
              </small>
            </div>
            <div style={{
              marginTop: '15px',
              padding: '12px',
              backgroundColor: '#fff9c4',
              borderRadius: '4px',
              fontSize: '14px'
            }}>
              <strong>💡 How to pay:</strong>
              <ol style={{ margin: '8px 0 0 0', paddingLeft: '20px' }}>
                <li>Open Google Pay / PhonePe / Paytm</li>
                <li>Send ₹{formData.timeSlot ? timeSlots.find(s => s.value === formData.timeSlot)?.price : '___'} to: <strong>123456789@ybl</strong></li>
                <li>Add reference: <strong>{upiReference}</strong> in remarks</li>
                <li>Click "Confirm Booking" below after payment</li>
              </ol>
            </div>
          </div>
        )} */}

        <div className="form-group">
          <label htmlFor="guestName">{t.yourName} *</label>
          <input
            type="text"
            id="guestName"
            name="guestName"
            value={formData.guestName}
            onChange={handleChange}
            placeholder="John Doe"
            required
          />
        </div>

        <div className="form-group">
          <label htmlFor="guestEmail">{t.email} *</label>
          <input
            type="email"
            id="guestEmail"
            name="guestEmail"
            value={formData.guestEmail}
            onChange={handleChange}
            placeholder="john@example.com"
            required
          />
        </div>

        <div className="form-group">
          <label htmlFor="guestPhone">{t.phone} *</label>
          <input
            type="tel"
            id="guestPhone"
            name="guestPhone"
            value={formData.guestPhone}
            onChange={handleChange}
            placeholder="+91 98765 43210"
            required
          />
        </div>

        <div className="form-group">
          <label htmlFor="notes">{t.notes}</label>
          <textarea
            id="notes"
            name="notes"
            value={formData.notes}
            onChange={handleChange}
            placeholder={t.notesPlaceholder}
            rows="3"
          />
        </div>

        <button type="submit" className="submit-btn" disabled={submitting}>
          {submitting ? t.booking : `📝 ${t.confirmBooking}`}
        </button>
      </form>

      {result && (
        <div className={`result ${result.success ? 'success' : 'error'}`}>
          <p>{result.success ? '✓' : '✗'} {result.message}</p>
          {result.booking && (
            <div className="booking-details">
              <p><strong>Booking ID:</strong> {result.booking.id}</p>
              <p><strong>Status:</strong> {result.booking.status}</p>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

// Wrapper component to provide Stripe Elements context
function BookingWidget(props) {
  return (
    <Elements stripe={stripePromise}>
      <BookingForm {...props} />
    </Elements>
  )
}

export default BookingWidget
